import { computed, inject, Injectable, signal } from '@angular/core';
import {
  WizardSiteSelectionComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/wizard-site-selection.component';
import {
  InvestmentTypeSelectionComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/investment-type-selection.component';
import { TranslateService } from '@ngx-translate/core';
import {
  AssetSelectionComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/asset-selection.component';
import { ComponentType } from '@angular/cdk/overlay';
import {
  BudgetAllocationComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/budget-allocation.component';
import {
  GainEnergyCarbonComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/gain-energy-carbon.component';
import {
  InvestmentReasonComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/investment-reason.component';
import {
  InvestmentImpactComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/investment-impact.component';
import {
  InvestmentFurtherInformationComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/further-information.component';
import {
  InvestmentSubmitComponent,
  InvestmentSubmitForm,
} from '../../@theme/components/wizards/investment-wizard/steps/investment-submit.component';
import {
  MonoPerimeterSelectionComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/mono-perimeter-selector.component';
import {
  BudgetForm,
  InitiativeForm,
  InvestmentNotationStepState,
  NomenclatureTechniqueStepState,
} from '../../@theme/components/wizards/investment-wizard/wizard-interface';
import { APIService, KPI } from '../api';
import {
  NewAssetWizardState,
  WizardNewAssetStepComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/wizard-new-asset-step.component';
import { InvestmentsService } from './investments.service';
import { BehaviorSubject, Observable, of, zip } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { InitiativesService } from './initiatives.service';
import {
  WizardInvestmentNotationStepComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/wizard-investment-notation-step.component';
import {
  WizardInvestmentCategoryComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/wizard-investment-category-step.component';
import {
  WizardNomenclatureTechniqueComponent,
} from '../../@theme/components/wizards/investment-wizard/steps/wizard-nomenclature-technique.component';
import { idMapToArray, ScopeService } from './scope.service';
import { SaveToastService } from './save-toast.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfigService } from './config.service';
import { InvestmentReasonsService } from './investment-reasons.service';

export type WizardStepConfig = Partial<{
  'SITE_SELECTION': KPI.Perimeter
  'INVESTMENT_TYPE_SELECTION': KPI.InvestmentType
  'ASSET_SELECTION': KPI.Asset
  'BUDGET_ALLOCATION': BudgetForm
  'GAIN_ENERGY_CARBON': InitiativeForm
  'INVESTMENT_REASON_SELECTION': KPI.InvestmentReason[]
  'INVESTMENT_IMPACT_SELECTION': KPI.RoadmapIndicator[]
  'INVESTMENT_FURTHER_INFORMATION_SELECTION': KPI.FurtherInformation
  'INVESTMENT_SUBMIT_STEP': InvestmentSubmitForm
  'MONO_PERIMETER_SELECTION': KPI.Perimeter
  'NEW_ASSET_STEP': NewAssetWizardState
  'INVESTMENT_NOTATION_STEP': InvestmentNotationStepState,
  // standalone steps, which will not be in the steps list
  'INVESTMENT_CATEGORY_SELECTION': KPI.InvestmentCategory,
  'NOMENCLATURE_TECHNIQUE_SELECTION': NomenclatureTechniqueStepState,
  'LINKED_TO': number;
  'ORIGIN_ASSET': KPI.Asset;
}>;

export type WizardStep = {
  key: keyof WizardStepConfig;
  label: string;
  component: ComponentType<any>; // Replace 'any' with the actual type of your component if available
  prevStep: WizardStep['key'] | null | (() => WizardStep['key']);
  nextStep?: WizardStep['key'] | (() => WizardStep['key']);
  isTheEnd?: boolean;
}

type StepIssue = {
  isBlocked: boolean;
  message?: string;
}

@Injectable()
export class InvestmentWizardService {

  private translate = inject(TranslateService);
  private invService = inject(InvestmentsService);
  private iniService = inject(InitiativesService);
  private api = inject(APIService);
  private scope = inject(ScopeService);
  private config = inject(ConfigService);
  private saveToast = inject(SaveToastService);

  private investmentReasonsService = inject(InvestmentReasonsService);

  private assetTypes = toSignal(this.scope.assetTypes$.pipe(idMapToArray()), { initialValue: [] });
  private subCategories = toSignal(this.scope.subCategories$.pipe(idMapToArray()), { initialValue: [] });
  private energyModuleEnabled = this.config.getSettingValue('ENERGY__ENABLE', false);
  private furtherInformations = toSignal(this.scope.getInvestFurtherInfo$.pipe(idMapToArray()), { initialValue: [] });
  private investmentReasons = toSignal(this.investmentReasonsService.investmentReasons$, { initialValue: [] });
  investmentReasonMandatory = computed(() =>
    this.config.getSettingValue('SYSTEM__INVESTMENT_REASON_MANDATORY', false)
  );

  public investmentId: number = null;
  public existingInvestment: KPI.MappedInvestment = null;
  public saveAndClose$ = new BehaviorSubject<boolean>(false);

  stepsConfig: WizardStep[] = [
    {
      key: 'SITE_SELECTION',
      label: this.translate.instant('Concerned site'),
      component: WizardSiteSelectionComponent,
      nextStep: 'INVESTMENT_TYPE_SELECTION',
      prevStep: null,
    },
    {
      key: 'INVESTMENT_TYPE_SELECTION',
      label: this.translate.instant('Investment Type'),
      component: InvestmentTypeSelectionComponent,
      nextStep: () => {
        const selectedType = this.getState('INVESTMENT_TYPE_SELECTION');
        const originAsset = this.getState('ORIGIN_ASSET');
        if (originAsset) {
          this.setState('ASSET_SELECTION', originAsset);
          return (selectedType.replacement || selectedType.instruction_next_asset) ? 'NEW_ASSET_STEP' : 'BUDGET_ALLOCATION';
        }
        return selectedType.creation_of_equipment ? 'MONO_PERIMETER_SELECTION' : 'ASSET_SELECTION';
      },
      prevStep: 'SITE_SELECTION',
    },
    {
      key: 'ASSET_SELECTION',
      label: this.translate.instant('Equipment related to the investment'),
      component: AssetSelectionComponent,
      nextStep: () => {
        const ivType = this.getState('INVESTMENT_TYPE_SELECTION');
        const assetState = this.getState('ASSET_SELECTION');
        if (!assetState) {
          return 'MONO_PERIMETER_SELECTION';
        } else {
          if (ivType.instruction_next_asset) {
            return 'NEW_ASSET_STEP';
          } else {
            return 'BUDGET_ALLOCATION';
          }
        }
      },
      prevStep: 'INVESTMENT_TYPE_SELECTION',
    },
    {
      key: 'NEW_ASSET_STEP',
      label: this.translate.instant('Future equipment'),
      component: WizardNewAssetStepComponent,
      nextStep: () => {
        return 'BUDGET_ALLOCATION';
      },
      prevStep: () => {
        if (this.getState('ORIGIN_ASSET')) {
          return 'INVESTMENT_TYPE_SELECTION';
        }
        const selectedType = this.getState('INVESTMENT_TYPE_SELECTION');
        return selectedType.creation_of_equipment ? 'MONO_PERIMETER_SELECTION' : 'ASSET_SELECTION';
      }
    },
    {
      key: 'MONO_PERIMETER_SELECTION',
      label: this.translate.instant('Mono Perimeter'),
      component: MonoPerimeterSelectionComponent,
      nextStep: () => {
        const ivType = this.getState('INVESTMENT_TYPE_SELECTION');
        if (ivType.instruction_next_asset || ivType.creation_of_equipment) {
          return 'NEW_ASSET_STEP';
        }
        return 'BUDGET_ALLOCATION';
      },
      prevStep: 'INVESTMENT_TYPE_SELECTION',
    },
    {
      key: 'BUDGET_ALLOCATION',
      label: this.translate.instant('Budget'),
      component: BudgetAllocationComponent,
      nextStep: () => {
        const ivType = this.getState('INVESTMENT_TYPE_SELECTION'),
          selectedAsset = this.getState('ASSET_SELECTION');
        // if the investment is related to current asset,
        // if asset => go to notation step
        // else     => go to nomenclature technique step
        if (ivType.maintenance || ivType.renew) {
          if (selectedAsset) return 'INVESTMENT_NOTATION_STEP';
          return 'NOMENCLATURE_TECHNIQUE_SELECTION';
        }
        if (!this.energyModuleEnabled) return this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : 'INVESTMENT_IMPACT_SELECTION';
        return 'GAIN_ENERGY_CARBON'
      },
      prevStep: () => {
        const ivType = this.getState('INVESTMENT_TYPE_SELECTION');
        if (ivType.instruction_next_asset) {
          return 'NEW_ASSET_STEP';
        }
        if (this.getState('ORIGIN_ASSET')) {
          return 'INVESTMENT_TYPE_SELECTION';
        }
        return 'MONO_PERIMETER_SELECTION';
      }
    },
    {
      key: 'NOMENCLATURE_TECHNIQUE_SELECTION',
      label: this.translate.instant('Technical Nomenclature'),
      component: WizardNomenclatureTechniqueComponent,
      nextStep: () => {
        if (!this.energyModuleEnabled) return this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : 'INVESTMENT_IMPACT_SELECTION';
        return 'GAIN_ENERGY_CARBON'
      },
      isTheEnd: false,
      prevStep: 'BUDGET_ALLOCATION',
    },
    {
      key: 'INVESTMENT_NOTATION_STEP',
      label: 'Impact de l’invest. sur l’équipement',
      component: WizardInvestmentNotationStepComponent,
      nextStep: () => {
        if (!this.energyModuleEnabled) return this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : 'INVESTMENT_IMPACT_SELECTION';
        return 'GAIN_ENERGY_CARBON'
      },
      prevStep: 'BUDGET_ALLOCATION',
    },
    {
      key: 'GAIN_ENERGY_CARBON',
      label: this.translate.instant('Gain Energy & Carbon'),
      component: GainEnergyCarbonComponent,
      nextStep: () => this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : 'INVESTMENT_IMPACT_SELECTION',
      prevStep: () => {
        const ivType = this.getState('INVESTMENT_TYPE_SELECTION'),
          assetState = this.getState('ASSET_SELECTION');
        if (ivType.renew || ivType.maintenance) {
          if (assetState) return 'INVESTMENT_NOTATION_STEP';
          return 'NOMENCLATURE_TECHNIQUE_SELECTION';
        }
        return 'BUDGET_ALLOCATION';
      },
    },
    {
      key: 'INVESTMENT_REASON_SELECTION',
      label: this.translate.instant('Add a tag'),
      component: InvestmentReasonComponent,
      nextStep: 'INVESTMENT_IMPACT_SELECTION',
      prevStep: () => {
        return this.energyModuleEnabled ? 'GAIN_ENERGY_CARBON' : 'BUDGET_ALLOCATION';
      },
    },
    {
      key: 'INVESTMENT_IMPACT_SELECTION',
      label: this.translate.instant('Specify the impact of the investment'),
      component: InvestmentImpactComponent,
      nextStep: () => {
        return this.furtherInformations()?.length > 0 ? 'INVESTMENT_FURTHER_INFORMATION_SELECTION' : 'INVESTMENT_SUBMIT_STEP'
      },
      prevStep: () => this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : (this.energyModuleEnabled ? 'GAIN_ENERGY_CARBON' : 'BUDGET_ALLOCATION'),
    },
    {
      key: 'INVESTMENT_FURTHER_INFORMATION_SELECTION',
      label: this.translate.instant('Additional information'),
      component: InvestmentFurtherInformationComponent,
      nextStep: 'INVESTMENT_SUBMIT_STEP',
      prevStep: () => {
        return this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : (this.energyModuleEnabled ? 'GAIN_ENERGY_CARBON' : 'BUDGET_ALLOCATION');
      },
    },
    {
      key: 'INVESTMENT_SUBMIT_STEP',
      label: this.translate.instant('New investment sheet'),
      component: InvestmentSubmitComponent,
      nextStep: null,
      isTheEnd: true,
      prevStep: () => {
        if (this.furtherInformations()?.length > 0) return 'INVESTMENT_FURTHER_INFORMATION_SELECTION';
        const prevOfImpact = this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : (this.energyModuleEnabled ? 'GAIN_ENERGY_CARBON' : 'BUDGET_ALLOCATION');
        return prevOfImpact;
      },
    },
    {
      key: 'INVESTMENT_CATEGORY_SELECTION',
      label: this.translate.instant('investment_category'),
      component: WizardInvestmentCategoryComponent,
      nextStep: null,
      isTheEnd: false,
      prevStep: null,
    },
  ];

  private currStepValid = signal<StepIssue>({ isBlocked: true, message: 'INITIAL' });

  private currentStepIdx = signal(0);

  // hold the state of every step. This state will later use to create the investment, action, asset, notations.
  private stateSignal = signal<WizardStepConfig>({});

  private skippedSteps: Set<string> = new Set();

  private setStep(idx: number) {
    if (idx < 0 || idx >= this.stepsConfig.length) {
      throw new Error('Step index out of bounds');
    }
    this.currentStepIdx.set(idx);
  }

  isSubmitting = signal(false);

  currentStep = computed(() => {
    return this.stepsConfig[this.currentStepIdx()];
  });

  // if we are on the last step, show submit button
  showSubmit = computed(() => this.currentStep().isTheEnd);

  // if we are not on the last step, show next button
  showNext = computed(() => {
    return !this.currentStep().isTheEnd;
  });

  // if we are not on the first step, show prev button
  showPrev = computed(() => this.currentStepIdx() > 0);

  isPrevDisabled = computed(() => {
    return !!this.getState('ORIGIN_ASSET') && this.currentStep().key === 'INVESTMENT_TYPE_SELECTION';
  });

  // to make sure that if we close the dialog, no state leaves behind
  stateIsEmpty = computed(() => Object.keys(this.stateSignal()).length == 0);

  getStepFromKey(key: WizardStep['key']) {
    return this.stepsConfig.findIndex(s => s.key === key);
  }

  getState<T extends keyof WizardStepConfig = keyof WizardStepConfig>(key: T): WizardStepConfig[T] {
    return this.stateSignal()[key];
  }

  setState<T extends keyof WizardStepConfig = keyof WizardStepConfig>(key: T, value: WizardStepConfig[T]) {
    this.stateSignal.update(s => ({ ...s, [key]: value }));
  }

  // if a step is changed, remove all states depending on it
  removeStates(keys: typeof this.stepsConfig[number]['key'][]) {
    this.stateSignal.update(s => {
      const next = { ...s };
      keys.forEach(k => delete next[k]);
      return next;
    });
  }

  onDestroy() {
    this.stateSignal.set({});
    this.currentStepIdx.set(0);
    this.skippedSteps.clear();
  }

  public prefill(state: WizardStepConfig, startStepKey?: keyof WizardStepConfig) {
    this.onDestroy();
    Object.keys(state).forEach(key => {
      this.setState(key as any, state[key]);
    });

    if (startStepKey) {
      const stepIdx = this.getStepFromKey(startStepKey);
      if (stepIdx !== -1) {
        this.currentStepIdx.set(stepIdx);
      }
    }
  }

  // go to step from key
  next(toStep: WizardStep['key'] = null) {
    if (toStep) {
      const stepIdx = this.getStepFromKey(toStep);
      this.setStep(stepIdx);
      return;
    }
    // Logic to move to the next step
    const currStep = this.currentStep();
    if (typeof currStep.nextStep === 'string') {
      this.setStep(this.getStepFromKey(currStep.nextStep));
      return;
    }
    try {
      const nextStepKey = currStep.nextStep();
      this.setStep(this.getStepFromKey(nextStepKey));
    } catch (e) {
      throw new Error('Error determining the next step with ambiguous: ' + e);
    }
  }

  canGoNext() {
    return this.currStepValid();
  }

  blockCurrentStep(message?: string) {
    this.currStepValid.set({ isBlocked: true, message });
  }

  unblockCurrentStep() {
    this.currStepValid.set({ isBlocked: false });
  }

  skipCurrentStep() {
    const currStep = this.currentStep();
    this.skippedSteps.add(currStep.key);
    this.next();
  }

  prev() {
    const currStep = this.currentStep(),
      prevStep = currStep.prevStep;

    let prevKey: WizardStep['key'];
    if (typeof prevStep === 'string') {
      prevKey = prevStep;
    } else if (typeof prevStep === 'function') {
      prevKey = prevStep();
    } else {
      prevKey = null;
    }

    if (prevKey && this.skippedSteps.has(prevKey)) {
      if (prevKey === 'INVESTMENT_IMPACT_SELECTION') {
        prevKey = this.investmentReasons()?.length > 0 ? 'INVESTMENT_REASON_SELECTION' : (this.energyModuleEnabled ? 'GAIN_ENERGY_CARBON' : 'BUDGET_ALLOCATION');
      }
    }

    if (prevKey) {
      this.setStep(this.getStepFromKey(prevKey));
    }
  }

  // main submission function
  submit() {
    // 1. create investment
    return this.createInvestment().pipe(
      switchMap(inv => zip([
        // 2. create asset replacement if any
        this.createAssetReplacement(inv),
        // 3. create initiative
        this.createInitiative(inv),
        // 4. create investment notation
        this.createInvestmentNotationAndDurationDeviation(inv),
        // 5. create new asset in case invtype=creation_of_equipment
        this.createNewAsset(inv),
      ])),
      tap(() => {
        this.saveToast.showSaved();
      }),
      map(() => true),
    );
  }

  createInvestment(): Observable<KPI.Investment> {
    const invSubmit: { label: string; } = this.getState('INVESTMENT_SUBMIT_STEP'),
      budget = this.getState('BUDGET_ALLOCATION'),
      investmentImpacts = this.getState('INVESTMENT_IMPACT_SELECTION') || [],
      furtherInformation = this.getState('INVESTMENT_FURTHER_INFORMATION_SELECTION'),
      invType = this.getState('INVESTMENT_TYPE_SELECTION'),
      asset = this.getState('ASSET_SELECTION'),
      monoPerimeter = this.getState('MONO_PERIMETER_SELECTION'),
      investmentCategory = this.getState('INVESTMENT_CATEGORY_SELECTION'),
      nomenclatureTechnique = this.getState('NOMENCLATURE_TECHNIQUE_SELECTION'),
      newAsset = this.getState('NEW_ASSET_STEP'),
      investmentNotation = this.getState('INVESTMENT_NOTATION_STEP');

    // hasNoImpact is true when investment notation step not enabled or no value is set
    const hasNoImpact = !(investmentNotation && investmentNotation.enabled);
    const reasons = (this.getState('INVESTMENT_REASON_SELECTION') || []).map(reason => reason.id);

    const investmentData = {
      // billing_type: 0,
      budget_origin: budget.budget_origin?.id,
      // budget_reference: undefined,
      budget_scopes: budget.budget_scopes ? [budget.budget_scopes.id] : [],
      price_sheet: budget.price_sheet_id,
      priority: budget.priority.id,
      // category: 0,
      // comments: '',
      // created_on: '',
      // documents: [],
      // duration_deviation: 0,
      // energy_saving: undefined,
      // execution: 0,
      final_schedule: budget.final_schedule_to,
      final_schedule_to: budget.final_schedule_to,
      further_information: furtherInformation?.id,
      has_no_impact: hasNoImpact,
      impact_roadmap_indicators: investmentImpacts.map(i => i.id),
      import_source: 'DECISION_WIZARD_INVESTMENT_FORM',
      investment_category: investmentCategory?.id,
      investment_type: invType.id,
      is_capex: budget.is_capex,
      // local_id: '',
      projects: [],
      // quantity: 1,
      reasons: reasons || [],
      // slices: [],
      spending_slices: [],
      sponsorships: [],
      status: budget.status.id,
      // sub_category: 0,
      // supplier: 0,
      // taxonomy_category: undefined,
      // taxonomy_objective: undefined,
      label: invSubmit.label,
      slices: [
        {
          id: null,
          price: budget.price,
          year: budget.final_schedule_to,
          status: budget.status.id,
        },
      ],
      linked_to: this.getState('LINKED_TO'),
    };
    // if there's no asset associated, we need to link the investment to the mono perimeter building
    // and set the nomenclature technique
    if (invType.creation_of_equipment || !asset) {
      investmentData['building'] = monoPerimeter.building_id;
    }
    if (invType.creation_of_equipment && newAsset) {
      const { category, sub_category, super_category } = newAsset.value;
      investmentData['category'] = category?.id;
      investmentData['sub_category'] = sub_category?.id;
      investmentData['super_category'] = super_category?.id;
    }
    if (!!asset) investmentData['asset'] = asset.id;
    if (nomenclatureTechnique && !asset) {
      investmentData['category'] = nomenclatureTechnique.category?.id;
      investmentData['sub_category'] = nomenclatureTechnique.sub_category?.id;
      investmentData['super_category'] = nomenclatureTechnique.super_category?.id;
    } else if ((invType.maintenance || invType.renew) && asset) {
      const assetType = asset.asset_type,
        subCategoryId = this.assetTypes().find(at => at.id === assetType)?.parent,
        categoryId = this.subCategories().find(sc => sc.id === subCategoryId)?.parent;
      investmentData['category'] = categoryId;
      investmentData['sub_category'] = subCategoryId;
    }
    const linkedTo = this.getState('LINKED_TO');
    if (linkedTo) {
      return this.invService.createComplementaryInvestment(linkedTo, <Partial<KPI.Investment>><unknown>investmentData);
    }
    return this.invService.createInvestment(<Partial<KPI.Investment>><unknown>investmentData);
  }

  createNewAsset(investment: KPI.Investment) {
    const newAssetState = this.getState('NEW_ASSET_STEP'),
      monoPerimeter = this.getState('MONO_PERIMETER_SELECTION'),
      budgetState = this.getState('BUDGET_ALLOCATION'),
      ivType = this.getState('INVESTMENT_TYPE_SELECTION');
    if (!newAssetState || !ivType.creation_of_equipment) return of(null);
    console.debug('Creating new asset for investment (type: creation_of_equipment)', investment);

    const assetType = newAssetState.value.asset_type;
    const defaultZone = (assetType as any)?.default_zone;
    const payload = {
      'perimeter': monoPerimeter.id,
      'zone': typeof defaultZone === 'object' ? defaultZone?.id : defaultZone,
      'asset_type': newAssetState.value.asset_type.id,
      'installation_year': budgetState.final_schedule_to,
      'zone_details': '',
      'quantity': newAssetState.value.quantity,
      'label': newAssetState.value.label,
      'comments': '',
      'duration_deviation': 0,
      // 'brand': null,
      // 'model': null,
      // 'power_source': null,
      // 'power': null,
      // 'served_zone': null,
      // 'parent': null,
      // 'ownership_type': 7,
      // 'notation_missing_reason': 0,
      // 'notation_missing_comment': '',
      // 'floor': 97,
      // 'planned_resale': 0,
      // 'planned_recycle': 0,
      // 'recycle_value': null,
      // 'resale_value': null,
      // 'height': null,
      'expert_mode': {},
      // 'refrigerant_type': null,
      // 'latest_refrigerant_declaration': null,
      // 'latest_energy_yield_declaration': null,
      // 'heat_transfer_coefficient_u': null,
      // 'thermal_resistance_coefficient_r': null,
      // 'source_info_heat_transfer_coefficient_u': null,
      // 'source_info_thermal_resistance_coefficient_r': null,
      // 'equipment_count': null,
      'notes_pictures': {},
      'maintenance_code': '',
      'step': KPI.ASSET_STEP.FUTURE,
    };
    return this.api.audit_asset_create(<Partial<KPI.Asset>><unknown>payload, { fields: 'id' }).pipe(
      switchMap(asset => this.api.investments_investments_patch(investment.id, { next_asset: asset.id })),
    );
  }

  createInvestmentNotationAndDurationDeviation(investment: KPI.Investment, existingInvestment?: boolean) {
    const investmentTypeState = this.getState('INVESTMENT_TYPE_SELECTION'),
      notationState = this.getState('INVESTMENT_NOTATION_STEP'),
      selectedAsset = this.getState('ASSET_SELECTION');
    // only create notation if the investment is related to current asset and notation step is enabled
    if (
      !(investmentTypeState.renew || investmentTypeState.maintenance || existingInvestment) ||
      (!notationState || !notationState.enabled)
    ) {
      console.debug('Investment type is not renew or maintenance, nor having notation value skipping notation creation');
      return of(null);
    }

    const createInvestmentNotation$ = (auditNotation: {
      notes: { question_item: number, note: number, mandatory: boolean, rating_reasons: any[], comments: string }[]
    }) => {
      return this.api.investments_investment_create_notation(
        selectedAsset.id,
        investment.id,
        auditNotation,
      ).pipe(
        tap((backendResponse) => console.log('Investment notation created', backendResponse)),
      );
    };

    const updateDurationDeviation$ = (dd: number, lifetime_after_investment: number) => {
      return this.api.investments_investments_patch(investment.id, {
        duration_deviation: dd,
        lifetime_after_investment: lifetime_after_investment,
      });
    };

    const convertNoteToSliderNode = (note: number): number => {
      if (note === 0) return 0;// dead
      if (note <= 22) return 4;//critical
      if (note <= 46) return 7;// bad
      if (note <= 70) return 10; // acceptable
      if (note <= 94) return 13;// good
      return 14;
    };

    const notation$ = this.scope.auditQuestions$.pipe(
      idMapToArray(),
      map(auditQuestions => auditQuestions.find(q => q.mnemonic === 'technical_state')),
      tap(auditQuestion => console.log('AUDIT QUESTION', auditQuestion)),
      filter(auditQuestion => !!auditQuestion),
      switchMap(auditQuestion => {
        const items = auditQuestion.items,
          firstItem = items[0],
          note = convertNoteToSliderNode(notationState.note);
        const auditNotation = {
          notes: [
            {
              question_item: firstItem.id,
              note: note, // set the note to the first item by default
              mandatory: false,
              rating_reasons: [],
              comments: null,
            },
          ],
        };
        return createInvestmentNotation$(auditNotation);
      }),
    );

    const durationDeviation$ = updateDurationDeviation$(notationState.duration_deviation, notationState.lifetime_after_investment);

    return notation$.pipe(
      // notation must be set before setting the duration deviation
      switchMap(() => durationDeviation$),
    );
  }

  createAssetReplacement(investment: KPI.Investment) {
    const selectedAsset = this.getState('ASSET_SELECTION'),
      nextAssetState = this.getState('NEW_ASSET_STEP'),
      nextAssetValue = nextAssetState?.value,
      invType = this.getState('INVESTMENT_TYPE_SELECTION');
    // if investment type is not replacement, then we don't create the replacement asset
    if (!invType.replacement) return of(null);
    console.debug('Creating asset replacement for investment (type: replacement)', investment);
    const updateInvestmentNextAsset$ = (nextAssetId: number) => {
      return this.api.investments_investments_patch(investment.id, {
        next_asset: nextAssetId,
      }).pipe(
        tap(() => console.log('Investment updated with next asset')),
      );
    }
    const payload: any = {
      'perimeter': (<KPI.Perimeter>selectedAsset.perimeter)?.id,
      'zone': selectedAsset.zone,
      'asset_type': nextAssetValue?.asset_type?.id || selectedAsset.asset_type,
      'installation_year': investment.final_schedule_to,
      'zone_details': selectedAsset.zone_details,
      'quantity': nextAssetState?.value?.quantity || selectedAsset.quantity,
      'label': nextAssetState?.value?.label,
      'comments': selectedAsset.label,
      'duration_deviation': 0,
      // "brand": "",
      // "model": "",
      // "power_source": null,
      // "power": "",
      // "served_zone": "",
      'parent': selectedAsset.parent,
      'ownership_type': null,
      // "notation_missing_reason": 5,
      'notation_missing_comment': '',
      'floor': selectedAsset.floor,
      'step': KPI.ASSET_STEP.FUTURE,
      // "local_id": "1760687713700"
    };
    // Ensure zone is a valid number, not null/undefined
    // Handle case where zone might be an object {id: number} instead of just a number
    if (!payload.zone || typeof payload.zone !== 'number') {
      let zoneId: number | null = null;
      if (typeof payload.zone === 'object' && payload.zone !== null) {
        zoneId = payload.zone.id;
      }
      if (!zoneId) {
        // Fall back to asset_type's default_zone if available
        const assetTypeId = nextAssetValue?.asset_type?.id || selectedAsset.asset_type;
        const assetType = this.assetTypes().find(at => at.id === assetTypeId);
        if (assetType && 'default_zone' in assetType) {
          const defaultZone = (assetType as any).default_zone;
          if (defaultZone) {
            zoneId = typeof defaultZone === 'object' ? defaultZone.id : defaultZone;
          }
        }
      }
      if (zoneId) {
        payload.zone = zoneId;
      } else {
        payload.zone = null;
      }
    }
    return this.api.audit_asset_create(<Partial<KPI.Asset>><unknown>payload, { fields: 'id' }).pipe(
      switchMap(asset => updateInvestmentNextAsset$(asset.id))
    );
  }

  createInitiative(investment: KPI.Investment, perimeterId: number = null): Observable<KPI.Initiative> {
    const site = this.getState('SITE_SELECTION'),
      gainsState = this.getState('GAIN_ENERGY_CARBON'),
      assetState = this.getState('ASSET_SELECTION'),
      monoPerimeter = this.getState('MONO_PERIMETER_SELECTION'),
      defaultConfidenceOnFeasibility = gainsState?.initiative_type?.default_confidence_on_feasibility;
    let status = investment.status;
    if (typeof investment.status === 'object') {
      status = (<{ id: number }><unknown>status)?.id;
    } else if (typeof investment.status === 'number') {
      status = investment.status;
    } else {
      console.error('Investment status is neither object nor number:', investment.status);
    }
    if (!gainsState || !gainsState.enabled) return of(null);
    if (assetState && assetState.perimeter) {
      perimeterId = (<KPI.Perimeter>assetState.perimeter).id;
    } else if (monoPerimeter) {
      perimeterId = monoPerimeter.id;
    } else if (!perimeterId) {
      console.error('No perimeter found for initiative creation');
    }
    const payload = {
      'label': investment.label,
      'status': status,
      'quantity': 1,
      'initiative_type': gainsState.initiative_type.id,
      'investment': investment.id,
      'confidence_on_feasibility': defaultConfidenceOnFeasibility,
      'perimeter': perimeterId,
      'savings_start_at': `${investment.final_schedule_to}-01-01`,
      'cee_bonus': 0,
      'yearly_cost_savings': 0,
      'responsible': gainsState.responsible,
      'gains': gainsState.gains,
      'site': site,
      'tags': [],
      'description': gainsState.description,
      'initially_proposed_by': gainsState.initially_proposed_by,
    };
    return this.iniService.createInitiative(<Partial<KPI.Initiative>><unknown>payload).pipe(
      switchMap(ini => this.iniService.updateInitiative(ini.id, {
        id: ini.id,
        // @ts-ignore
        investment: investment.id,
      })),
    );
  }
}
